// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@keller/App/Components/Common/XngZone";
import { getIsNonPurchasable } from "@keller/App/Store/Selectors/GeneralSelectors";
import color from "@keller/XDL/Utilities/color";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    const session = getSession(state);
    const variantTraits = getProductState(state, ownProps.parentProductId).value?.variantTraits;
    const variantChildren = getVariantChildrenDataView(state, ownProps.parentProductId).value;
    const hasVariants = variantTraits && variantTraits.length > 0 && variantChildren && variantChildren.length > 0;
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const isNonPurchasable = getIsNonPurchasable(state);
    return {
        isAuthenticated,
        hasVariants,
        isNonPurchasable,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasParentProductId & HasShellContext;

export interface ProductDetailsActionsTopSectionStyles {
    wrapper?: InjectableCss;
}

export const productDetailsActionsTopSectionStyles: ProductDetailsActionsTopSectionStyles = {
    wrapper: {
        css: css`
            background: ${color("common.ui12")};
            padding: 16px;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
        `,
    },
};

const styles = productDetailsActionsTopSectionStyles;

const ProductDetailsActionsTopSection: FC<Props> = ({
    id,
    isAuthenticated,
    hasVariants,
    isNonPurchasable,
    shellContext: { isInShell },
}) => {
    const showUnAuthSection = isInShell || !isAuthenticated;
    const showNonPurchasableSection = isInShell || (isAuthenticated && isNonPurchasable);
    const showVariantsSection = isInShell || hasVariants;
    if (!isInShell && isAuthenticated && !hasVariants && !isNonPurchasable) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            {showUnAuthSection && <XngZone contentId={id} zoneName="Content0" hint="Zone For Un-Authenticated" />}
            {showNonPurchasableSection && (
                <XngZone contentId={id} zoneName="Content1" hint="Zone For Non-Purchasable" />
            )}
            {showVariantsSection && <XngZone contentId={id} zoneName="Content2" hint="Zone For Variant Options" />}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, null)(withIsInShell(ProductDetailsActionsTopSection))),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
